// Max featured tiles shown in the row on any breakpoint.
// On desktop the row holds at most 11 tiles total:
// up to MAX_FEATURED_ROW featured tiles + the "View All" tile.
// Note: the visual_filter metaobject's Order field should have a
// maximum value of 11 in Shopify admin to align with this limit.
const MAX_FEATURED_ROW = 11;

class VisualFacetsContainer extends HTMLElement {
  connectedCallback() {
    this._list = this.querySelector('.visual-facets__options');
    this._viewAllTile = this._list.querySelector('.visual-facets__view-all-tile');
    this._openBtn = this._list.querySelector('[data-vf-open-drawer]');
    this._items = [...this._list.querySelectorAll('.visual-facets__option')];

    this._drawer = this.querySelector('[data-vf-drawer]');
    this._drawerInner = this._drawer?.querySelector('.drawer__inner');
    this._drawerList = this._drawer?.querySelector('[data-vf-drawer-list]');
    this._searchInput = this._drawer?.querySelector('[data-vf-search]');
    this._searchDefault = this._drawer?.querySelector('[data-vf-search-default]');
    this._searchClear = this._drawer?.querySelector('[data-vf-search-clear]');
    this._noResults = this._drawer?.querySelector('[data-vf-no-results]');

    this._escHandler = (e) => {
      if (e.key === 'Escape') this._closeDrawer();
    };

    this._showFeatured();

    this._openBtn?.addEventListener('click', () => this._openDrawer());

    // Close on overlay click, but not on inner panel click
    this._drawerInner?.addEventListener('click', (e) => e.stopPropagation());
    this._drawer?.addEventListener('click', () => this._closeDrawer());

    this._drawer?.querySelector('[data-vf-drawer-close]')?.addEventListener('click', () => this._closeDrawer());

    this._searchClear?.addEventListener('click', () => {
      this._searchInput.value = '';
      this._onSearch('');
      this._searchInput.focus();
    });

    this._searchInput?.addEventListener('input', () => {
      this._onSearch(this._searchInput.value.trim().toLowerCase());
    });

    document.addEventListener('keydown', this._escHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._escHandler);
  }

  _setVisible(item, visible) {
    item.hidden = !visible;
    const input = item.querySelector('input[type="checkbox"]');
    if (input) {
      // Keep checked checkboxes enabled so FormData includes them on next submission
      input.disabled = !visible && !input.checked;
    }
  }

  _showFeatured() {
    const featured = this._items
      .filter((item) => item.dataset.featured === 'true')
      .sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order))
      .slice(0, MAX_FEATURED_ROW);

    this._items.forEach((item) => this._setVisible(item, false));
    featured.forEach((item) => {
      this._setVisible(item, true);
      this._list.insertBefore(item, this._viewAllTile);
    });

    if (this._viewAllTile) {
      this._viewAllTile.hidden = featured.length >= this._items.length;
    }
  }

  _openDrawer() {
    this._buildDrawerItems();
    if (this._searchInput) this._searchInput.value = '';
    this._onSearch('');
    this._drawer.classList.add('active');
    this._lockBackgroundScroll();
    requestAnimationFrame(() => this._searchInput?.focus());
  }

  _closeDrawer() {
    if (!this._drawer?.classList.contains('active')) return;
    this._drawer.classList.remove('active');
    this._unlockBackgroundScroll();
    this._openBtn?.focus();
  }

  _lockBackgroundScroll() {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    if (!this._boundPreventTouchScroll) {
      this._boundPreventTouchScroll = (e) => {
        if (!e.target.closest('[data-vf-drawer-list]')) e.preventDefault();
      };
    }
    this._drawer?.removeEventListener('touchmove', this._boundPreventTouchScroll);
    this._drawer?.removeEventListener('wheel', this._boundPreventTouchScroll);
    this._drawer?.addEventListener('touchmove', this._boundPreventTouchScroll, { passive: false });
    this._drawer?.addEventListener('wheel', this._boundPreventTouchScroll, { passive: false });
  }

  _unlockBackgroundScroll() {
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    if (this._boundPreventTouchScroll) {
      this._drawer?.removeEventListener('touchmove', this._boundPreventTouchScroll);
      this._drawer?.removeEventListener('wheel', this._boundPreventTouchScroll);
    }
  }

  _buildDrawerItems() {
    if (!this._drawerList) return;
    this._drawerList.innerHTML = '';

    const sorted = [...this._items].sort((a, b) =>
      (a.dataset.label || '').localeCompare(b.dataset.label || '')
    );

    for (const sourceItem of sorted) {
      const checkbox = sourceItem.querySelector('input[type="checkbox"]');
      if (!checkbox) continue;

      const imgEl = sourceItem.querySelector('img');
      const label = sourceItem.dataset.label || '';
      const isUnavailable = checkbox.hasAttribute('data-unavailable') && !checkbox.checked;

      const li = document.createElement('li');
      li.className = 'vf-drawer__item';
      li.dataset.label = label.toLowerCase();
      if (checkbox.checked) li.classList.add('is-selected');
      if (isUnavailable) li.classList.add('is-unavailable');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vf-drawer__tile-btn';
      btn.setAttribute('aria-pressed', String(checkbox.checked));

      if (imgEl) {
        const media = document.createElement('span');
        media.className = 'vf-drawer__tile-media';
        const img = document.createElement('img');
        img.src = imgEl.src;
        img.alt = imgEl.alt;
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        media.appendChild(img);
        btn.appendChild(media);
      }

      const textSpan = document.createElement('span');
      textSpan.className = 'vf-drawer__tile-label';
      textSpan.textContent = label;
      btn.appendChild(textSpan);

      li.appendChild(btn);
      this._drawerList.appendChild(li);

      btn.addEventListener('click', () => {
        checkbox.disabled = false;
        const nowChecked = !checkbox.checked;
        checkbox.checked = nowChecked;

        // Re-apply disabled only if hidden AND unchecked
        if (sourceItem.hidden && !nowChecked) checkbox.disabled = true;

        li.classList.toggle('is-selected', nowChecked);
        btn.setAttribute('aria-pressed', String(nowChecked));

        checkbox.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
  }

  _onSearch(query) {
    if (!this._drawerList) return;

    if (this._searchDefault) this._searchDefault.hidden = query.length > 0;
    if (this._searchClear) this._searchClear.hidden = query.length === 0;

    const items = this._drawerList.querySelectorAll('.vf-drawer__item');
    let anyVisible = false;

    items.forEach((item) => {
      const match = !query || item.dataset.label.includes(query);
      item.hidden = !match;
      if (match) anyVisible = true;
    });

    if (this._noResults) this._noResults.hidden = anyVisible;
  }
}

customElements.define('visual-facets-container', VisualFacetsContainer);
